import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, effect } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { LtmsLoaderComponent } from 'src/app/shared/components/ltms-loader/ltms-loader.component';
import { ChannelSetupService, firstValidationMessage } from 'src/app/shared/services/channel-setup/channel-setup.service';
import { ChannelUnsubscribeEntry } from 'src/app/shared/services/channel-setup/channel-setup.models';

const TOAST_MS = 15000;

const SEARCH_FIELDS: { field_id: string; field_text: string }[] = [
  { field_id: 'email_address', field_text: 'Email Address' },
  { field_id: 'customer_id', field_text: 'Customer ID' },
  { field_id: 'phone_number', field_text: 'Phone' },
  { field_id: 'whatsapp_number', field_text: 'WhatsApp' },
];

@Component({
  selector: 'app-channel-unsubscribe',
  templateUrl: './channel-unsubscribe.component.html',
  styleUrls: [
    '../channel-blacklist/channel-blacklist.component.scss',
    './channel-unsubscribe.component.scss',
  ],
  standalone: true,
  imports: [CommonModule, FormsModule, LtmsLoaderComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChannelUnsubscribeComponent implements OnInit, OnDestroy {
  @Input() isWriteAllowed = false;

  brandId: string | null = null;
  loading = false;
  loadError = '';
  entries: ChannelUnsubscribeEntry[] = [];
  page = 1;
  pageSize = 10;
  hasMore = false;
  total: number | null = null;
  displayedTotal = 0;
  totalLoading = false;
  private totalAnimFrame = 0;
  entriesLoading = false;
  listSearchQuery = '';
  searchFields = SEARCH_FIELDS;
  selectedSearchField = SEARCH_FIELDS[0];
  showSearchFields = false;
  private appliedListSearch = '';
  private appliedSearchField = SEARCH_FIELDS[0].field_id;
  confirmRemove: ChannelUnsubscribeEntry | null = null;

  constructor(
    private readonly appStateService: AppStateService,
    private readonly channelSetupService: ChannelSetupService,
    private readonly toastService: ToastService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
  ) {
    effect(() => {
      if (!this.appStateService.initialized()) {
        return;
      }
      const authDetails: any = this.appStateService.managerAccess();
      const loggedIn = authDetails?.loggedInManager ?? {};
      const brandDetails: any = this.appStateService.brandDetails();
      const nextBrandId = loggedIn.brand_id || brandDetails?._id || null;
      if (nextBrandId && nextBrandId !== this.brandId) {
        this.brandId = nextBrandId;
        this.total = null;
        this.displayedTotal = 0;
        this.stopTotalAnimation();
        this.load();
      }
    });
  }

  ngOnInit(): void {
    this.host.nativeElement.addEventListener('click', this.onUnauthorizedClick, true);
    document.addEventListener('click', this.onDocumentClick, true);
  }

  ngOnDestroy(): void {
    this.stopTotalAnimation();
    this.host.nativeElement.removeEventListener('click', this.onUnauthorizedClick, true);
    document.removeEventListener('click', this.onDocumentClick, true);
  }

  private readonly onUnauthorizedClick = (event: Event): void => {
    if (this.isWriteAllowed) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target?.closest?.('.is-unauthorized')) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.toastService.showPopup('ERROR', 'Not authorized', 'Not authorized', TOAST_MS);
  };

  private readonly onDocumentClick = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    if (target?.closest?.('.unsub-search')) {
      return;
    }
    if (this.showSearchFields) {
      this.showSearchFields = false;
      this.changeDetector.markForCheck();
    }
  };

  get canEdit(): boolean {
    return this.isWriteAllowed;
  }

  get totalKnown(): boolean {
    return this.total != null;
  }

  get canLoadTotal(): boolean {
    return !this.totalKnown && (this.hasMore || Boolean(this.appliedListSearch));
  }

  get unsubscribedCaption(): string {
    if (this.total === 0) {
      return 'No customers on this list.';
    }
    return this.total === 1 ? 'Customer skipped on send' : 'Customers skipped on send';
  }

  get rangeStart(): number {
    if (!this.entries.length) {
      return 0;
    }
    return ((this.page - 1) * this.pageSize) + 1;
  }

  get rangeEnd(): number {
    if (!this.entries.length) {
      return 0;
    }
    return this.rangeStart + this.entries.length - 1;
  }

  get pageTotal(): number {
    if (this.total != null) {
      return this.total;
    }
    if (!this.hasMore) {
      return this.rangeEnd;
    }
    return this.rangeEnd;
  }

  get pageTotalKnown(): boolean {
    return this.total != null || !this.hasMore;
  }

  get pageCount(): number {
    if (this.total != null) {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    }
    if (!this.hasMore) {
      return Math.max(1, this.page);
    }
    return this.page + 1;
  }

  get canGoLastPage(): boolean {
    return this.total != null || !this.hasMore;
  }

  get searchNeedsEnter(): boolean {
    const next = String(this.listSearchQuery || '').trim();
    return Boolean(next) && (next !== this.appliedListSearch || this.selectedSearchField.field_id !== this.appliedSearchField);
  }

  get searchPlaceholder(): string {
    return `Search by ${this.selectedSearchField.field_text}`;
  }

  load(): void {
    this.page = 1;
    this.loadEntries();
  }

  loadEntries(): void {
    if (!this.brandId) {
      return;
    }
    this.loadError = '';
    this.entriesLoading = true;
    if (!this.entries.length) {
      this.loading = true;
    }
    const search = String(this.appliedListSearch || '').trim();
    this.channelSetupService.listUnsubscribeEntries(this.brandId, {
      ...(search ? { q: search, search_field: this.appliedSearchField } : {}),
      page: this.page,
      page_size: this.pageSize,
    }).subscribe({
      next: (data) => {
        this.entries = Array.isArray(data?.entries) ? data.entries : [];
        this.hasMore = Boolean(data?.has_more);
        this.page = Number(data?.page) || 1;
        this.inferTotalFromTable();
        this.entriesLoading = false;
        this.loading = false;
        this.changeDetector.markForCheck();
      },
      error: (error) => {
        this.entriesLoading = false;
        this.loading = false;
        this.loadError = firstValidationMessage(error) || 'Could not load the unsubscribe list.';
        this.changeDetector.markForCheck();
      },
    });
  }

  private inferTotalFromTable(): void {
    if (this.total != null || this.appliedListSearch || this.hasMore) {
      return;
    }
    this.total = this.rangeEnd;
    this.displayedTotal = this.total;
  }

  loadTotal(): void {
    if (!this.brandId || this.totalLoading) {
      return;
    }
    this.totalLoading = true;
    this.channelSetupService.countUnsubscribeEntries(this.brandId).subscribe({
      next: (data) => {
        this.total = Number(data?.total) || 0;
        this.totalLoading = false;
        this.animateTotal(this.total);
        this.changeDetector.markForCheck();
      },
      error: (error) => {
        this.totalLoading = false;
        this.toastService.showPopup('ERROR', 'Unsubscribe list', firstValidationMessage(error) || 'Could not load the total.', TOAST_MS);
        this.changeDetector.markForCheck();
      },
    });
  }

  private animateTotal(to: number): void {
    this.stopTotalAnimation();
    const target = Math.max(0, Number(to) || 0);
    this.displayedTotal = 0;
    if (!target) {
      this.changeDetector.markForCheck();
      return;
    }
    const start = performance.now();
    const duration = Math.min(1100, 420 + Math.log10(target) * 280);
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      this.displayedTotal = Math.round(target * eased);
      this.changeDetector.markForCheck();
      if (t < 1) {
        this.totalAnimFrame = requestAnimationFrame(step);
        return;
      }
      this.displayedTotal = target;
      this.changeDetector.markForCheck();
    };
    this.totalAnimFrame = requestAnimationFrame(step);
  }

  private stopTotalAnimation(): void {
    if (this.totalAnimFrame) {
      cancelAnimationFrame(this.totalAnimFrame);
      this.totalAnimFrame = 0;
    }
  }

  toggleSearchFields(event: Event): void {
    event.stopPropagation();
    this.showSearchFields = !this.showSearchFields;
  }

  chooseSearchField(field: { field_id: string; field_text: string }): void {
    this.selectedSearchField = field;
    this.showSearchFields = false;
    this.changeDetector.markForCheck();
  }

  onListSearchQueryChange(): void {
    const next = String(this.listSearchQuery || '').trim();
    if (!next && this.appliedListSearch) {
      this.clearListSearch();
      return;
    }
    this.changeDetector.markForCheck();
  }

  applyListSearch(): void {
    const next = String(this.listSearchQuery || '').trim();
    if (!next) {
      return;
    }
    this.appliedListSearch = next;
    this.appliedSearchField = this.selectedSearchField.field_id;
    this.page = 1;
    this.loadEntries();
  }

  clearListSearch(): void {
    this.listSearchQuery = '';
    this.appliedListSearch = '';
    this.page = 1;
    this.loadEntries();
  }

  askRemove(row: ChannelUnsubscribeEntry): void {
    if (!this.canEdit) {
      return;
    }
    this.confirmRemove = row;
  }

  confirmRemoveNow(): void {
    const row = this.confirmRemove;
    this.confirmRemove = null;
    if (!this.brandId || !row?.id) {
      return;
    }
    this.channelSetupService.deleteUnsubscribeEntry(this.brandId, row.id).subscribe({
      next: () => {
        this.toastService.showPopup('SUCCESS', 'Unsubscribe list', 'Customer can receive invitations from this project again.', TOAST_MS);
        if (this.total != null && this.total > 0) {
          this.total -= 1;
          this.displayedTotal = this.total;
        }
        this.loadEntries();
        this.changeDetector.markForCheck();
      },
      error: (error) => {
        this.toastService.showPopup('ERROR', 'Unsubscribe list', firstValidationMessage(error) || 'Could not remove this customer.', TOAST_MS);
        this.changeDetector.markForCheck();
      },
    });
  }

  goToFirstPage(): void {
    if (this.page <= 1) {
      return;
    }
    this.page = 1;
    this.loadEntries();
  }

  goToLastPage(): void {
    if (!this.canGoLastPage || this.page >= this.pageCount) {
      return;
    }
    this.page = this.pageCount;
    this.loadEntries();
  }

  previousPage(): void {
    if (this.page <= 1) {
      return;
    }
    this.page -= 1;
    this.loadEntries();
  }

  nextPage(): void {
    if (this.page >= this.pageCount) {
      return;
    }
    this.page += 1;
    this.loadEntries();
  }

  unsubscribedOn(row: ChannelUnsubscribeEntry): Date | string | null {
    return row?.created_date || null;
  }

  displayName(row: ChannelUnsubscribeEntry): string {
    return String(row?.name || '').trim() || '—';
  }

  contactLine(row: ChannelUnsubscribeEntry): string {
    const phone = String(row?.phone_number || '').trim();
    const whatsapp = String(row?.whatsapp_number || '').trim();
    if (phone && whatsapp && phone !== whatsapp) {
      return `${phone} · WhatsApp ${whatsapp}`;
    }
    if (whatsapp) {
      return `WhatsApp ${whatsapp}`;
    }
    if (phone) {
      return phone;
    }
    return 'No phone';
  }
}
